import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ApAlert, ApAlertType } from './ApAlert';

//
// Provides a service for showing alert messages.
// Supports displaying error, success and warning alerts.
//
// based on https://github.com/cornflourblue/angular-8-alert-notifications
//
// To use, add the <alert></alert> tag where you want alerts to be displayed.
//    <ng-container>
//      div class="col-sm-12" >
//        <ap-alert></ap-alert>
//      </div>
//    </ng-container>
//
// then add providers for ApAlertService to the component using <ap-alert></ap-alert>
//  @Component({providers: [
//      ApAlertService,
//    ],
//    ...
//
// AlertComponent optional inputs
//  fade - controls if alert messages are faded out when closed. Defaults to true.
//
// ApAlert with no optional parameters: this.alertService.success('It worked!');
// ApAlert with optional parameter: this.alertService.success('Save completed!', { autoClose: true, autoCloseTimeout: 10000 });
//

@Injectable()
export class ApAlertService implements OnDestroy {

  private subject$ = new Subject<ApAlert>();
  private defaultId = 'default-alert';
  private defaultHtmlBaseId = 1;

  ngOnDestroy() {
    this.subject$.complete();
  }

  // Used by ApAlertComponent only
  onAlert(id = this.defaultId): Observable<ApAlert> {
    return this.subject$.asObservable().pipe(filter((x) => x && x.id === id));
  }

  success(message: string, options?: Partial<ApAlert>) {
    this.alert(new ApAlert({ ...options, type: ApAlertType.Success, message }));
  }

  info(message: string, options?: Partial<ApAlert>) {
    this.alert(new ApAlert({ ...options, type: ApAlertType.Info, message }));
  }

  error(message: string, options?: Partial<ApAlert>) {
    this.alert(new ApAlert({ ...options, type: ApAlertType.Error, message }));
  }

  warn(message: string, options?: Partial<ApAlert>) {
    this.alert(new ApAlert({ ...options, type: ApAlertType.Warning, message }));
  }

  alert(alert: ApAlert) {
    alert.id = alert.id || this.defaultId;
    alert.htmlBaseId = alert.htmlBaseId || (this.defaultHtmlBaseId++).toString();
    this.subject$.next(alert);
  }

  clearAllAlerts(id = this.defaultId) {
    this.subject$.next(new ApAlert({ id }));
  }
}
